import { useEffect, useLayoutEffect, useState } from "react";
import { Link, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { sendContactMessage } from "./api/contact";
import { fetchPublicPlanCatalog } from "./api/planCatalog";
import { resolvePlanDisplayPricing } from "./utils/planDisplayPricing";
import { LegalDocument } from "./components/LegalDocument";
import { SUPPORT_EMAIL } from "./config/contact";
import {
  getCachedTurnstileSiteKey,
  loadTurnstileSiteKey,
} from "./config/turnstilePublic";
import { useTurnstile } from "./hooks/useTurnstile";
import { LanguageProvider, useLanguage } from "./i18n/LanguageContext";
import { InscriptionPage } from "./pages/InscriptionPage";
import { VerifyEmailPage } from "./pages/VerifyEmailPage";
import "./App.css";

/** Reset scroll on route change; scroll to #section when hash is present. */
function ScrollManager() {
  const { pathname, hash } = useLocation();

  useLayoutEffect(() => {
    if (hash) {
      const id = decodeURIComponent(hash.replace(/^#/, ""));
      const tryScroll = () => {
        const el = document.getElementById(id);
        if (!el) return false;
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        return true;
      };
      if (tryScroll()) return undefined;
      const t = window.setTimeout(tryScroll, 80);
      return () => window.clearTimeout(t);
    }
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    return undefined;
  }, [pathname, hash]);

  return null;
}

function scrollToSection(id) {
  const el = document.getElementById(id);
  if (el) {
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  }
}

function FlagFr() {
  return (
    <svg className="lang-flag-icon" viewBox="0 0 3 2" aria-hidden="true">
      <rect width="1" height="2" fill="#002395" />
      <rect x="1" width="1" height="2" fill="#fff" />
      <rect x="2" width="1" height="2" fill="#ED2939" />
    </svg>
  );
}

function FlagUs() {
  return (
    <svg className="lang-flag-icon" viewBox="0 0 19 13" aria-hidden="true">
      <rect width="19" height="13" fill="#B22234" />
      <rect y="1" width="19" height="1" fill="#fff" />
      <rect y="3" width="19" height="1" fill="#fff" />
      <rect y="5" width="19" height="1" fill="#fff" />
      <rect y="7" width="19" height="1" fill="#fff" />
      <rect y="9" width="19" height="1" fill="#fff" />
      <rect y="11" width="19" height="1" fill="#fff" />
      <rect width="8" height="7" fill="#3C3B6E" />
    </svg>
  );
}

function LanguageSwitcher() {
  const { lang, setLang, t } = useLanguage();

  return (
    <div className="lang-dropdown">
      <div className="lang-select-wrap">
        <span className="lang-select-flag">{lang === "fr" ? <FlagFr /> : <FlagUs />}</span>
        <select
          id="site-lang"
          className="lang-select"
          value={lang}
          onChange={(e) => setLang(e.target.value)}
          aria-label={t.nav.language}
        >
          <option value="fr">{t.nav.french}</option>
          <option value="en">{t.nav.english}</option>
        </select>
      </div>
    </div>
  );
}

function Layout({ children, hideFooter = false }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactMessage, setContactMessage] = useState("");
  const [contactBusy, setContactBusy] = useState(false);
  const [contactError, setContactError] = useState("");
  const [contactSent, setContactSent] = useState(false);
  const [turnstileSiteKey, setTurnstileSiteKey] = useState(() =>
    getCachedTurnstileSiteKey()
  );
  const turnstile = useTurnstile(contactOpen ? turnstileSiteKey : "");
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const currentYear = new Date().getFullYear();

  useEffect(() => {
    if (!contactOpen) return undefined;
    let cancelled = false;
    void loadTurnstileSiteKey().then((key) => {
      if (!cancelled && key) setTurnstileSiteKey(key);
    });
    return () => {
      cancelled = true;
    };
  }, [contactOpen]);

  const closeMenu = () => setMenuOpen(false);

  const goHome = () => {
    closeMenu();
    if (location.pathname === "/") {
      window.scrollTo({ top: 0, behavior: "smooth" });
      if (location.hash) navigate("/", { replace: true });
      return;
    }
    navigate("/");
  };

  const goSection = (sectionId) => {
    closeMenu();
    if (location.pathname === "/") {
      if (location.hash !== `#${sectionId}`) {
        navigate(`/#${sectionId}`, { replace: true });
      }
      // Scroll immediately while Accueil is already mounted
      window.requestAnimationFrame(() => scrollToSection(sectionId));
      return;
    }
    navigate(`/#${sectionId}`);
  };

  const isSignupPage =
    location.pathname === "/inscription" ||
    location.pathname === "/signup" ||
    location.pathname === "/verify-email";

  return (
    <div className={`page${hideFooter || isSignupPage ? " page-signup" : ""}`}>
      <header className="topbar">
        <div className="brand" role="button" tabIndex={0} onClick={goHome} onKeyDown={(e) => e.key === "Enter" && goHome()}>
          <img src="/images/talo-logo.png" alt="Logo Talo" className="brand-logo" />
          <div>
            <p className="brand-name">{t.brand.name}</p>
            <p className="brand-sub">{t.brand.tagline}</p>
          </div>
        </div>
        <nav className={`nav ${menuOpen ? "nav-open" : ""}`}>
          <button type="button" className="nav-text-btn" onClick={goHome}>
            {t.nav.home}
          </button>
          <button type="button" className="nav-text-btn" onClick={() => goSection("apropos")}>
            {t.nav.about}
          </button>
          <button type="button" className="nav-text-btn" onClick={() => goSection("benefices")}>
            {t.nav.benefits}
          </button>
          <button type="button" className="nav-text-btn" onClick={() => goSection("pricing")}>
            {t.nav.pricing}
          </button>
          <Link to="/politique-legale" onClick={closeMenu}>
            {t.nav.legal}
          </Link>
          <Link
            to="/inscription?plan=premium"
            className="nav-signup-btn"
            onClick={closeMenu}
          >
            {t.nav.signup}
          </Link>
          <LanguageSwitcher />
        </nav>
        <div className="top-icons">
          <button
            type="button"
            className="menu-toggle"
            aria-label={t.nav.openMenu}
            onClick={() => setMenuOpen((v) => !v)}
          >
            {menuOpen ? "\u00D7" : "\u2630"}
          </button>
        </div>
      </header>
      <main className="main-content">{children}</main>
      {hideFooter ? null : (
      <footer className="modern-footer">
  <div className="footer-container">
    {/* LEFT */}
    <div className="footer-brand">
      <div className="footer-logo-wrap">
        <img
          src="/images/talo-logo.png"
          alt="Talo Logo"
          className="footer-logo"
        />

        <div>
          <h3>{t.brand.name}</h3>
          <p>{t.brand.tagline}</p>
        </div>
      </div>

      <p className="footer-description">{t.footer.description}</p>

      <div className="footer-cta-block">
        <p className="footer-cta-title">{t.footer.ctaTitle}</p>
        <p className="footer-cta-text">{t.footer.ctaText}</p>
        <div className="footer-cta-actions">
          <Link to="/inscription?plan=premium" className="footer-signup-btn">
            {t.footer.signup}
          </Link>
          <button
            type="button"
            className="footer-plans-btn"
            onClick={() => goSection("pricing")}
          >
            {t.footer.viewPlans}
          </button>
        </div>
      </div>

      {/* SOCIALS */}
      <div className="footer-socials">
        <a href="#" aria-label="Facebook">
          <i className="fa-brands fa-facebook-f"></i>
        </a>

        <a href="#" aria-label="Instagram">
          <i className="fa-brands fa-instagram"></i>
        </a>

        <a href="#" aria-label="LinkedIn">
          <i className="fa-brands fa-linkedin-in"></i>
        </a>

        <a href="#" aria-label="WhatsApp">
          <i className="fa-brands fa-whatsapp"></i>
        </a>
      </div>

      {/* STORES */}
      <div className="footer-store-buttons">
        <a
          href="https://apps.apple.com/us/app/talo/6767144859"
          target="_blank"
          rel="noreferrer"
        >
          <img
            src={t.hero.appStoreBadge}
            alt={t.hero.appStoreAlt}
          />
        </a>

        <a
          href="https://play.google.com/store/apps/details?id=com.kalyx.talo"
          target="_blank"
          rel="noreferrer"
        >
          <img
            src={t.hero.playStoreBadge}
            alt={t.hero.playStoreAlt}
          />
        </a>
        {/* WINDOWS */}
        <a
          className="windows-download-btn-footer"
          href="https://github.com/jamesjaly007/talo-web/releases/download/v1.0.0/TaloSetup.exe"
          target="_blank"
          rel="noreferrer"
        >
          <div className="windows-icon">
            <i className="fa-brands fa-windows"></i>
          </div>

          <div className="windows-text">
            <span>{t.footer.downloadOn}</span>
            <strong> {t.footer.windows}</strong>
          </div>
        </a>
      </div>
    </div>

    {/* CENTER */}
    <div className="footer-column">
      <h4>{t.footer.solutions}</h4>

      {t.footer.solutionItems.map((item) => (
        <span key={item}>{item}</span>
      ))}
    </div>

    {/* RIGHT */}
    <div className="footer-column">
      <h4>{t.footer.contact}</h4>

      <a href="tel:+243823170355">
        +243 823 170 355
      </a>

      <a href={`mailto:${SUPPORT_EMAIL}`}>
        {SUPPORT_EMAIL}
      </a>

      <span>{t.footer.location}</span>

      <button
        type="button"
        className="footer-contact-btn"
        onClick={() => setContactOpen(true)}
      >
        {t.footer.contactUs}
      </button>
    </div>
  </div>

  {/* BOTTOM */}
  <div className="footer-bottom">
    <p>
      {"\u00A9"} {currentYear} Talo {"\u2014"} {t.footer.rights}
    </p>

    <div className="footer-bottom-links">
      <Link to="/politique-legale">
        {t.footer.privacy}
      </Link>

      <Link to="/politique-legale">
        {t.footer.terms}
      </Link>
    </div>
  </div>
</footer>
      )}
      {contactOpen ? (
        <div className="contact-modal-backdrop" role="presentation" onClick={() => setContactOpen(false)}>
          <div
            className="contact-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="contact-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="contact-modal-shell">
              <div className="contact-modal-head">
                <div>
                  <p className="contact-modal-kicker">Talo</p>
                  <h3 id="contact-modal-title">{t.contact.title}</h3>
                </div>
                <button
                  type="button"
                  className="contact-close"
                  onClick={() => setContactOpen(false)}
                  aria-label={t.contact.close}
                >
                  {"\u00D7"}
                </button>
              </div>
              <div className="contact-modal-body">
                <p className="contact-modal-sub">{t.contact.subtitle}</p>
                <p className="contact-modal-hint">
                  {t.contact.hintPrefix}{" "}
                  <strong>{SUPPORT_EMAIL}</strong>
                  {t.contact.hintSuffix ? ` ${t.contact.hintSuffix}` : ""}
                </p>
                {contactSent ? (
                  <p className="contact-modal-success" role="status">
                    {t.contact.success}
                  </p>
                ) : (
                <form
                  className="contact-form"
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (contactBusy) return;
                    const formEl = e.currentTarget;
                    const hp = String(
                      new FormData(formEl).get("company") ?? ""
                    ).trim();
                    if (turnstile.enabled && !turnstile.token) {
                      setContactError(t.contact.captchaRequired);
                      return;
                    }
                    setContactError("");
                    setContactBusy(true);
                    void (async () => {
                      const res = await sendContactMessage({
                        name: contactName,
                        email: contactEmail,
                        message: contactMessage,
                        company: hp,
                        turnstileToken: turnstile.token,
                      });
                      setContactBusy(false);
                      turnstile.reset();
                      if (!res.ok) {
                        setContactError(res.error || t.contact.error);
                        return;
                      }
                      setContactSent(true);
                      setContactName("");
                      setContactEmail("");
                      setContactMessage("");
                      window.setTimeout(() => {
                        setContactOpen(false);
                        setContactSent(false);
                        setContactError("");
                      }, 2200);
                    })();
                  }}
                >
                  {/* honeypot — bots often fill this; humans leave empty */}
                  <input
                    type="text"
                    name="company"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    defaultValue=""
                    style={{ position: "absolute", left: "-9999px", height: 0, width: 0, opacity: 0 }}
                  />
                  <div className="contact-form-row">
                    <label>
                      {t.contact.name}
                      <input
                        type="text"
                        name="name"
                        placeholder={t.contact.namePlaceholder}
                        value={contactName}
                        onChange={(e) => setContactName(e.target.value)}
                        required
                        disabled={contactBusy}
                        maxLength={120}
                      />
                    </label>
                    <label>
                      {t.contact.email}
                      <input
                        type="email"
                        name="email"
                        placeholder={t.contact.emailPlaceholder}
                        value={contactEmail}
                        onChange={(e) => setContactEmail(e.target.value)}
                        required
                        disabled={contactBusy}
                        maxLength={160}
                      />
                    </label>
                  </div>
                  <label>
                    {t.contact.message}
                    <textarea
                      name="message"
                      rows={4}
                      placeholder={t.contact.messagePlaceholder}
                      value={contactMessage}
                      onChange={(e) => setContactMessage(e.target.value)}
                      required
                      disabled={contactBusy}
                      maxLength={4000}
                    />
                  </label>
                  {turnstile.enabled ? (
                    <div className="contact-turnstile">
                      <div ref={turnstile.hostRef} />
                      {!turnstile.ready ? (
                        <span className="contact-modal-hint">{t.contact.captchaLoading}</span>
                      ) : null}
                    </div>
                  ) : null}
                  {contactError ? (
                    <p className="contact-modal-error" role="alert">
                      {contactError}
                    </p>
                  ) : null}
                  <div className="contact-form-actions">
                    <button
                      type="button"
                      className="contact-cancel"
                      onClick={() => {
                        setContactOpen(false);
                        setContactError("");
                        setContactSent(false);
                      }}
                      disabled={contactBusy}
                    >
                      {t.contact.cancel}
                    </button>
                    <button type="submit" className="contact-submit" disabled={contactBusy}>
                      {contactBusy ? t.contact.sending : t.contact.submit}
                    </button>
                  </div>
                </form>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function PricingPlanCard({
  plan,
  catalog,
  featured,
  perMonth,
  promoLabel,
  popularLabel,
  ctaLabel,
  ctaTo,
  ctaClassName,
}) {
  const pricing = resolvePlanDisplayPricing(catalog);
  const displayLabel = pricing.displayLabel || plan.oldPrice || plan.price;
  const listLabel = pricing.showPromo ? pricing.listLabel : null;

  return (
    <div className={featured ? "pricing-card pricing-card-featured" : "pricing-card"}>
      {featured ? <div className="pricing-badge">{popularLabel}</div> : null}
      <p className={featured ? "pricing-plan pricing-plan-featured" : "pricing-plan"}>
        {plan.name}
      </p>
      <div className="pricing-price-wrapper">
        {listLabel ? <p className="pricing-old-price">{listLabel}</p> : null}
        <h3 className={featured ? "pricing-price pricing-price-featured" : "pricing-price"}>
          {displayLabel}
          <span>{perMonth}</span>
        </h3>
        {pricing.showPromo ? (
          <p className="pricing-discount-badge">{promoLabel}</p>
        ) : null}
      </div>
      <ul className={featured ? "pricing-features pricing-features-featured" : "pricing-features"}>
        {plan.features.map((feature) => (
          <li key={feature}>{"\u2713"} {feature}</li>
        ))}
      </ul>
      <Link className={ctaClassName} to={ctaTo}>
        {ctaLabel}
      </Link>
    </div>
  );
}

function Accueil() {
  const { t } = useLanguage();
  const { standard, premium, enterprise } = t.pricing.plans;
  const [pricingByCode, setPricingByCode] = useState({});

  useEffect(() => {
    let cancelled = false;
    void fetchPublicPlanCatalog().then((res) => {
      if (cancelled || !res.ok) return;
      setPricingByCode(res.byCode ?? {});
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <Layout>
      <section className="hero-clean" id="apropos">
        <div className="hero-copy">
          <h1>{t.hero.title}</h1>
          <p>{t.hero.subtitle}</p>

          <div className="hero-cta-row">
            <Link className="hero-cta-primary" to="/inscription?plan=trial">
              {t.hero.ctaPrimary}
            </Link>
            <a className="hero-cta-secondary" href="#pricing">
              {t.hero.ctaSecondary}
            </a>
          </div>

          <div className="store-buttons">
            {/* APP STORE */}
            <a
              className="store-btn app-store-btn"
              href="https://apps.apple.com/us/app/talo/6767144859"
              target="_blank"
              rel="noreferrer"
              aria-label={t.hero.appStoreAria}
            >
              <img
                className="store-badge"
                src={t.hero.appStoreBadge}
                alt={t.hero.appStoreAlt}
              />
            </a>

            {/* GOOGLE PLAY */}
            <a
              className="store-btn play-store-btn"
              href="https://play.google.com/store/apps/details?id=com.kalyx.talo"
              target="_blank"
              rel="noreferrer"
              aria-label={t.hero.playStoreAria}
            >
              <img
                className="store-badge"
                src={t.hero.playStoreBadge}
                alt={t.hero.playStoreAlt}
              />
            </a>

            {/* WINDOWS */}
            <a
              className="windows-download-btn"
              href="https://github.com/jamesjaly007/talo-web/releases/download/v1.0.0/TaloSetup.exe"
              target="_blank"
              rel="noreferrer"
            >
              <div className="windows-icon">
                <i className="fa-brands fa-windows"></i>
              </div>

              <div className="windows-text">
                <span>{t.footer.downloadOn}</span>
                <strong> {t.footer.windows}</strong>
              </div>
            </a>
          </div>

          <p className="store-helper store-helper-below">{t.hero.helper}</p>
        </div>

        <div className="hero-visual">
          <img
            className="hero-responsive-image"
            src="/images/responsive.png"
            alt={t.hero.imageAlt}
          />
        </div>
      </section>

      <section className="benefits-section" id="benefices">
        <div className="benefits-header">
          <h2>{t.benefits.title}</h2>

          <p className="section-intro">{t.benefits.intro}</p>
        </div>

        <div className="benefits-grid">
          {t.benefits.items.map((item) => (
            <article className="benefit-card" key={item.title}>
              <span className="material-symbols-outlined benefit-icon">{item.icon}</span>
              <h4>{item.title}</h4>
              <p>{item.text}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="pricing-section" id="pricing">
        <div className="pricing-header">
          <h2>{t.pricing.title}</h2>
          <p>{t.pricing.subtitle}</p>
        </div>

        <div className="pricing-grid">
          <PricingPlanCard
            plan={standard}
            catalog={pricingByCode.standard}
            perMonth={t.pricing.perMonth}
            promoLabel={t.pricing.promo}
            popularLabel={t.pricing.popular}
            ctaLabel={t.pricing.start}
            ctaTo="/inscription?plan=standard"
            ctaClassName="pricing-btn"
          />
          <PricingPlanCard
            plan={premium}
            catalog={pricingByCode.premium}
            featured
            perMonth={t.pricing.perMonth}
            promoLabel={t.pricing.promo}
            popularLabel={t.pricing.popular}
            ctaLabel={t.pricing.choosePremium}
            ctaTo="/inscription?plan=premium"
            ctaClassName="pricing-btn pricing-btn-featured"
          />
          <PricingPlanCard
            plan={enterprise}
            catalog={pricingByCode.enterprise}
            perMonth={t.pricing.perMonth}
            promoLabel={t.pricing.promo}
            popularLabel={t.pricing.popular}
            ctaLabel={t.pricing.contactUs}
            ctaTo="/inscription?plan=enterprise"
            ctaClassName="pricing-btn"
          />
        </div>
      </section>
    </Layout>
  );
}


function PolitiqueLegaleTalo() {
  const { t } = useLanguage();
  return (
    <Layout>
      <LegalDocument legal={t.taloLegal} />
    </Layout>
  );
}

function PolitiqueLegaleWapi() {
  const { t } = useLanguage();
  return (
    <Layout>
      <LegalDocument legal={t.wapiLegal} />
    </Layout>
  );
}

function Inscription() {
  return <InscriptionPage Layout={Layout} />;
}

function VerifyEmail() {
  return <VerifyEmailPage Layout={Layout} />;
}

function App() {
  return (
    <LanguageProvider>
      <ScrollManager />
      <Routes>
        <Route path="/" element={<Accueil />} />
        <Route path="/inscription" element={<Inscription />} />
        <Route path="/signup" element={<Inscription />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/politique-legale" element={<PolitiqueLegaleTalo />} />
        <Route path="/politique-legale/talo" element={<PolitiqueLegaleTalo />} />
        <Route path="/politique-legale/wapi" element={<PolitiqueLegaleWapi />} />
      </Routes>
    </LanguageProvider>
  );
}

export default App;
